import { AxiosInstance } from 'axios';

import { FormName } from '@topconagriculture/feed-types';

import { APIManagerConfig } from '../apiManager';
import {
  Concentrate,
  DeliveryDataItem,
  LoadDataItem,
  LoadDeliveryData,
  Milking,
  MilkPrice,
  PriceItem,
} from '../../../store/entities/dataTools/dataTools.types';

const DataToolsServiceFactory = ({ HTTPBase, config }: { HTTPBase: AxiosInstance; config: APIManagerConfig }) => {
  return {
    getLoadsDeliveriesBatches: (
      daysBack: number,
      endDate: Date,
      includeDeliveries = false,
      filter?: string,
      groupIds?,
      groupTypeIds?,
    ): Promise<any> => {
      return HTTPBase.get(`farm/${config.farmId}/batches`, {
        params: {
          daysBack,
          endDate,
          groupIds,
          groupTypeIds,
          includedeliveries: includeDeliveries,
          filter,
        },
      }).then((res) => res.data);
    },
    getLoadsDeliveriesData: (batchId: string): Promise<any> => {
      return HTTPBase.get(`farm/${config.farmId}/batches/${batchId}`).then((res) => res.data);
    },
    updateLoad: (
      batchId: string,
      loadId: string,
      data: Partial<LoadDataItem>,
      ignoreStock: boolean,
      formName: FormName,
    ): Promise<any> => {
      return HTTPBase.patch(
        `farm/${config.farmId}/batches/${batchId}/loads/${loadId}?ignoreStock=${ignoreStock}`,
        data,
        {
          headers: { formName },
        },
      ).then((res) => res.data);
    },
    updateDelivery: (
      batchId: string,
      deliveryId: string,
      data: Partial<DeliveryDataItem>,
      ignoreStock: boolean,
      formName: FormName,
    ): Promise<any> => {
      return HTTPBase.patch(
        `farm/${config.farmId}/batches/${batchId}/deliveries/${deliveryId}?ignoreStock=${ignoreStock}`,
        data,
        {
          headers: { formName },
        },
      ).then((res) => res.data);
    },
    updateBatch: (batchId: number, data: LoadDeliveryData, formName: FormName): Promise<any> => {
      return HTTPBase.patch(`farm/${config.farmId}/batches/${batchId}`, data, { headers: { formName } }).then(
        (res) => res.data,
      );
    },
    deleteLoadDeliveryBatch: (batchId: string, formName: FormName, ignorestock: boolean): Promise<any> => {
      return HTTPBase.delete(`farm/${config.farmId}/batches/${batchId}?ignorestock=${ignorestock}`, {
        headers: { formName },
      }).then((res) => res.data);
    },
    getIngredientPrices: async (ingredientId: string): Promise<any> => {
      return HTTPBase.get(`farm/${config.farmId}/ingredients/${ingredientId}/prices`).then((res) => res.data);
    },
    updateIngredientPrice: async (
      ingredientId: string,
      priceId: string,
      data: Partial<PriceItem>,
      formName: FormName,
    ): Promise<any> => {
      return HTTPBase.patch(`farm/${config.farmId}/ingredients/${ingredientId}/prices/${priceId}`, data, {
        headers: { formName },
      }).then((res) => res.data);
    },
    addIngredientPrice: async (ingredientId: string, data: Partial<PriceItem>, formName: FormName): Promise<any> => {
      return HTTPBase.post(`farm/${config.farmId}/ingredients/${ingredientId}/prices`, data, {
        headers: { formName },
      }).then((res) => res.data);
    },
    getMilkings: (daysBack: number, endDate: Date): Promise<any> => {
      return HTTPBase.get(`farm/${config.farmId}/hms/milkings`, {
        params: {
          daysBack,
          endDate,
        },
      }).then((res) => res.data);
    },
    createMilking: (milking: Partial<Milking>, formName: FormName): Promise<Milking> => {
      return HTTPBase.post(`farm/${config.farmId}/hms/milkings`, milking, { headers: { formName } }).then(
        (res) => res.data,
      );
    },
    updateMilking: (id: string, milking: Partial<Milking>, formName: FormName): Promise<Milking> => {
      return HTTPBase.patch(`farm/${config.farmId}/hms/milkings/${id}`, milking, { headers: { formName } }).then(
        (res) => res.data,
      );
    },
    getConcentrates: (daysBack: number, endDate: Date): Promise<Concentrate[]> =>
      HTTPBase.get(`farm/${config.farmId}/hms/concentrates`, {
        params: {
          daysBack,
          endDate,
        },
      }).then((res) => res.data),
    createConcentrate: (concentrate: Partial<Concentrate>, formName: FormName): Promise<Concentrate> =>
      HTTPBase.post(`farm/${config.farmId}/hms/concentrates`, concentrate, { headers: { formName } }).then(
        (res) => res.data,
      ),
    updateConcentrate: (id: string, concentrate: Partial<Concentrate>, formName: FormName): Promise<Concentrate> =>
      HTTPBase.patch(`farm/${config.farmId}/hms/concentrates/${id}`, concentrate, { headers: { formName } }).then(
        (res) => res.data,
      ),
    getMilkPrices: (): Promise<MilkPrice> =>
      HTTPBase.get(`farm/${config.farmId}/hms/milkprices`, {
        params: {
          daysBack: 99999,
        },
      }).then((res) => res.data),
    createMilkPrice: (milkPrice: Partial<MilkPrice>, formName: FormName): Promise<MilkPrice> =>
      HTTPBase.post(`farm/${config.farmId}/hms/milkprices`, milkPrice, { headers: { formName } }).then(
        (res) => res.data,
      ),
    updateMilkPrice: (id: string, milkPrice: Partial<MilkPrice>, formName: FormName): Promise<MilkPrice> =>
      HTTPBase.patch(`farm/${config.farmId}/hms/milkprices/${id}`, milkPrice, { headers: { formName } }).then(
        (res) => res.data,
      ),
    deleteMilkPrice: (id: string, formName: FormName): Promise<any> =>
      HTTPBase.delete(`farm/${config.farmId}/hms/milkprices/${id}`, { headers: { formName } }).then((res) => res.data),
    sendLoadsData: (data) => HTTPBase.post(`farm/${config.farmId}/feedings/loads`, data),
  };
};

export default DataToolsServiceFactory;
